import { Button } from '@/components/UI/Common/Admin/Button';
import { Checkbox } from '@/components/UI/Common/Admin/Checkbox';
import { LoadingSpinner } from '@/components/UI/Common/Admin/LoadingSpinner';
import { PageLayout } from '@/components/UI/Layouts/Admin/PageLayout';
import Table from '@/components/UI/Table/Table';
import { ROUTES } from '@/config/routes';
import { useDeleteMany } from '@/queries/admin';
import { IApplicationGroup } from '@/types/appliction';
import { TableColumn } from '@/types/table';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { Filters } from '../components/Filters';
import { useResourceList } from '@/hooks/useResourceList';
import { Pagination } from '@/components/UI/Pagination/Pagination';

export const ApplicationGroupsPage = () => {
  const {
    data: applicationGroups,
    isLoading,
    reset,
    handleFilterChange,
    handleSortChange,
    handlePageChange,
    handleLimitChange,
    filter,
    page,
    sortOrder,
    totalPages,
    totalItems,
    limit,
    sortBy,
  } = useResourceList('application-groups');
  const { mutateAsync: deleteApplicationGroups } = useDeleteMany('application-groups');
  const [idsToDelete, setIdsToDelete] = useState<string[]>([]);
  const navigate = useNavigate();

  const toggleDelete = () => {
    if (idsToDelete?.length === applicationGroups?.data.length) {
      setIdsToDelete([]);
    } else {
      setIdsToDelete(applicationGroups?.data.map((group) => group.id) || []);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteApplicationGroups({ ids: idsToDelete });
      toast.success('Группы успешно удалены');
      setIdsToDelete([]);
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Произошла ошибка при удалении группы');
    }
  };

  const renderCheckBox = (_val: unknown, row: IApplicationGroup) => (
    <Checkbox
      className="prevent-row-click"
      checked={idsToDelete?.includes(row.id)}
      onChange={() => {
        if (idsToDelete?.includes(row.id)) {
          setIdsToDelete(idsToDelete.filter((id) => id !== row.id));
        } else {
          setIdsToDelete([...idsToDelete, row.id]);
        }
      }}
    />
  );

  const columns: TableColumn<IApplicationGroup>[] = useMemo(
    () => [
      {
        key: '',
        title: (
          <Checkbox
            onChange={() => {}}
            onClick={toggleDelete}
            checked={idsToDelete?.length === applicationGroups?.data.length}
          />
        ),
        render: renderCheckBox,
      },
      {
        key: 'id',
        title: 'ID',
        sortable: true,
        sortBy: sortBy,
        sortOrder: sortOrder,
        onColumnHeaderClick: handleSortChange,
      },
      {
        key: 'title',
        title: 'Название группы',
        sortable: true,
        sortBy: sortBy,
        sortOrder: sortOrder,
        onColumnHeaderClick: handleSortChange,
      },
      {
        key: 'sort',
        title: 'Порядок',
        sortable: true,
        sortBy: sortBy,
        sortOrder: sortOrder,
        onColumnHeaderClick: handleSortChange,
      },
      {
        key: 'createdAt',
        title: 'Создана',
        sortable: true,
        sortBy: sortBy,
        sortOrder: sortOrder,
        onColumnHeaderClick: handleSortChange,
        render: (val: unknown) =>
          new Date(val as string).toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
      },
      {
        key: 'updatedAt',
        sortable: true,
        sortBy: sortBy,
        sortOrder: sortOrder,
        onColumnHeaderClick: handleSortChange,
        title: 'Обновлена',
        render: (val: unknown) =>
          new Date(val as string).toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
      },
    ],
    [applicationGroups?.data.length, idsToDelete?.length, sortBy, sortOrder],
  );

  if (isLoading || !applicationGroups) {
    return <LoadingSpinner className="h-full" text="Загрузка..." />;
  }

  return (
    <PageLayout
      actions={
        <div className="flex gap-4">
          {idsToDelete.length > 0 && (
            <Button onClick={handleDelete} size="md" variant="danger">
              Удалить
            </Button>
          )}
          <Link to={ROUTES.ADMIN.CREATE('application-groups')}>
            <Button size="md" variant="success">
              Создать
            </Button>
          </Link>
        </div>
      }
      title="Группы приложений"
    >
      <Filters value={filter} onChange={handleFilterChange} onReset={reset} />

      {applicationGroups.data.length > 0 && (
        <div className="overflow-x-auto">
          <Table
            data={applicationGroups.data}
            columns={columns}
            onRowClick={(row) => navigate(ROUTES.ADMIN.EDIT('application-groups', row.id))}
          />
        </div>
      )}

      <Pagination
        className="mt-10"
        onItemsPerPageChange={handleLimitChange}
        currentPage={page}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        itemsPerPage={limit}
      />
    </PageLayout>
  );
};
